import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { useUserProfileData } from '@/hooks/useUserProfileData';
import { createCRMObjectClient } from '@/lib/crm/core/CRMObjectClient';
import type { CrmObject } from '@/types/crm';

export type IntegrationVisibility = 'private' | 'org';

interface UseIntegrationVisibilityResult {
  /**
   * Set an integration entity's default data visibility. Returns true on
   * success. Works for any `ahoy_external_integration` entity (Gmail, Outlook).
   */
  updateVisibility: (
    entityId: string,
    visibility: IntegrationVisibility
  ) => Promise<boolean>;
  currentlyUpdatingEntity: string | null;
  visibilityError: string | null;
}

/**
 * Shared hook to update an integration entity's default data visibility via the
 * CRM events API. Provider-agnostic — both Gmail and Outlook integrations are
 * `ahoy_external_integration` entities, so the same write path serves both.
 *
 * Always bundles a clear of `ahoy_visibility_flipped_on_disconnect` ("false")
 * alongside the visibility change (mirrors the Gmail behaviour) so a user-
 * initiated change also dismisses any reconnect-visibility banner. The backend
 * property filter drops the flag clear if already false, so it is a safe no-op.
 */
export function useIntegrationVisibility(
  orgId: string | undefined
): UseIntegrationVisibilityResult {
  const queryClient = useQueryClient();
  const { data: profileData } = useUserProfileData();
  const userId = profileData?.user?.id;
  const [currentlyUpdatingEntity, setCurrentlyUpdatingEntity] = useState<
    string | null
  >(null);
  const [visibilityError, setVisibilityError] = useState<string | null>(null);

  const updateVisibility = useCallback(
    async (
      entityId: string,
      visibility: IntegrationVisibility
    ): Promise<boolean> => {
      if (!orgId || !userId || !entityId) {
        return false;
      }

      setCurrentlyUpdatingEntity(entityId);
      setVisibilityError(null);

      try {
        const queryKey = [
          'crm',
          parseInt(orgId),
          'objects',
          'ahoy_external_integration',
          'detail',
          entityId,
        ];
        let integrationObject = queryClient.getQueryData<CrmObject>(queryKey);

        if (!integrationObject) {
          const client = createCRMObjectClient(
            parseInt(orgId),
            'ahoy_external_integration'
          );
          integrationObject = await client.get(entityId);
          if (!integrationObject) {
            throw new Error('Integration not found');
          }
          queryClient.setQueryData(queryKey, integrationObject);
        }

        const { createEventBuilder } =
          await import('@/lib/crm/core/EventBuilder');
        const builder = createEventBuilder({
          sourceType: 'ahoy_user',
          sourceId: String(userId),
          startSeq: integrationObject.last_event_seq,
        });
        builder.propertiesUpdated(entityId, 'ahoy_external_integration', {
          ahoy_default_data_visibility: visibility,
          ahoy_visibility_flipped_on_disconnect: 'false',
        });

        const events = builder.build();
        const client = createCRMObjectClient(
          parseInt(orgId),
          'ahoy_external_integration'
        );
        await client.submitEvents({ events });

        queryClient.setQueryData<CrmObject>(queryKey, old => {
          if (!old) {
            return old;
          }
          return {
            ...old,
            last_event_seq: old.last_event_seq + 1,
            properties: {
              ...old.properties,
              ahoy_default_data_visibility: { value: visibility },
              ahoy_visibility_flipped_on_disconnect: { value: 'false' },
            },
          };
        });

        await queryClient.invalidateQueries({
          queryKey: ['crm', parseInt(orgId), 'objects'],
        });
        return true;
      } catch (err) {
        console.error(err);
        setVisibilityError("We couldn't update visibility. Try again.");
        return false;
      } finally {
        // Functional setter so an earlier-completing request doesn't clear
        // the loading marker for a later in-flight update of a different entity.
        setCurrentlyUpdatingEntity(prev => (prev === entityId ? null : prev));
      }
    },
    [orgId, userId, queryClient]
  );

  return { updateVisibility, currentlyUpdatingEntity, visibilityError };
}
